import { isEmpty, path } from 'ramda'

import { isAbortError } from '@common/utils'

const REVIEW_ANALYSIS_DATA_LOADING = 'REVIEW_ANALYSIS_DATA_LOADING'
const REVIEW_ANALYSIS_DATA_LOADED = 'REVIEW_ANALYSIS_DATA_LOADED'
const REVIEW_ANALYSIS_DATA_FAILED = 'REVIEW_ANALYSIS_DATA_FAILED'

const REVIEW_ANALYSIS_RESET_STATE = 'REVIEW_ANALYSIS_RESET_STATE'

const defaultState = {}

const entityName = 'reviewAnalysis'

async function fetchPropertiesFromGroups({ apiFetch, propertyGroupsSelectorData }) {
  const defaultPayload = { active: true, pageSize: 9999 }
  const accounts = []
  const groups = []

  propertyGroupsSelectorData.forEach((item) => {
    if (item.isAccount) {
      accounts.push(item.id)
    } else {
      groups.push(item.id)
    }
  })

  const result = []
  const propertiesFromAccounts = await apiFetch(
    '/properties/',
    {
      ...defaultPayload,
      account: accounts.join(','),
    },
    { cancelationPrefix: `${entityName}_acc` },
  )
  if (propertiesFromAccounts?.results) {
    result.push(...propertiesFromAccounts.results)
  }
  if (!isEmpty(groups)) {
    const propertiesFromGroups = await apiFetch(
      '/properties/',
      {
        ...defaultPayload,
        propertyGroup: groups.join(','),
      },
      { cancelationPrefix: `${entityName}_pg` },
    )
    if (propertiesFromGroups?.results) {
      result.push(...propertiesFromGroups.results)
    }
  }
  return result.map((item) => item.id)
}

export default {
  name: entityName,
  reducer: (state, action) => {
    if (action.type === REVIEW_ANALYSIS_RESET_STATE) {
      return defaultState
    }
    if (action.type === REVIEW_ANALYSIS_DATA_LOADING) {
      return { ...state, data: { ...action.meta, payload: action.payload } }
    }
    if (action.type === REVIEW_ANALYSIS_DATA_LOADED) {
      return { ...state, data: { ...action.meta, data: action.payload } }
    }
    if (action.type === REVIEW_ANALYSIS_DATA_FAILED) {
      return { ...state, data: { ...action.meta, error: action.payload } }
    }
    return state || defaultState
  },
  selectReviewAnalysisIsLoading: ({ reviewAnalysis }) => {
    const status = path(['data', 'status'], reviewAnalysis)
    return status === 'loading'
  },
  selectReviewAnalysis: ({ reviewAnalysis }) => path(['data', 'data'], reviewAnalysis),
  selectReviewAnalysisError: ({ reviewAnalysis }) =>
    path(['data', 'error'], reviewAnalysis),
  doResetReviewAnalysisState:
    () =>
    ({ dispatch }) =>
      dispatch({ type: REVIEW_ANALYSIS_RESET_STATE }),
  doFetchReviewAnalysis:
    (payload) =>
    async ({ dispatch, apiFetch }) => {
      try {
        dispatch({
          type: REVIEW_ANALYSIS_DATA_LOADING,
          payload,
          meta: { status: 'loading' },
        })

        const {
          eventStart,
          eventEnd,
          reviewPublishedStart,
          reviewPublishedEnd,
          organizations,
          accounts,
          propertyGroups,
          properties,
        } = payload

        const apiDateFormat = 'yyyy-MM-dd'

        let propertyIds = properties?.map((item) => item.id)
        const hasNoGroupEntities = propertyGroups?.some((item) => item.isAccount)
        if (hasNoGroupEntities) {
          const ids = await fetchPropertiesFromGroups({
            apiFetch,
            propertyGroupsSelectorData: propertyGroups,
          })
          propertyIds = ids
        }

        const response = await apiFetch(
          '/properties/review_dashboard/',
          {
            eventStart: eventStart.toFormat(apiDateFormat),
            eventEnd: eventEnd.toFormat(apiDateFormat),
            reviewPublishedStart: reviewPublishedStart.toFormat(apiDateFormat),
            reviewPublishedEnd: reviewPublishedEnd.toFormat(apiDateFormat),
            ...(!isEmpty(organizations)
              ? { organizations: organizations.map((org) => org.id) }
              : null),
            ...(!isEmpty(accounts)
              ? { accounts: accounts.map((acc) => acc.id) }
              : null),
            ...(!isEmpty(propertyGroups) && isEmpty(propertyIds)
              ? { propertyGroups: propertyGroups.map((pg) => pg.id) }
              : null),
            ...(!isEmpty(propertyIds) ? { properties: propertyIds } : null),
          },
          {
            cancelationPrefix: entityName,
          },
        )

        dispatch({
          type: REVIEW_ANALYSIS_DATA_LOADED,
          payload: response,
          meta: { status: 'succeeded' },
        })
        return response
      } catch (err) {
        if (!isAbortError(err)) {
          dispatch({
            type: REVIEW_ANALYSIS_DATA_FAILED,
            payload: err,
            meta: { status: 'failed' },
          })
          throw err
        }
        return null
      }
    },
}
